import React, { useState, useMemo, useEffect } from 'react';
import styles from '../styles.module.scss';
import cN from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@Redux/search/store';
import { setFilterData } from '@Redux/search/actions';
import { I_FilterOption } from '../interface';
import { isSameArr } from '../utils';

interface I_AlliancesProps {
    airlines?: I_FilterOption[];
    tempMFilter?: any;
    setTempMFilter?: Function;
}

interface I_Alliances {
    [key: string]: string[] | null;
    skyTeam: string[] | null;
    starAlliance: string[] | null;
    oneworld: string[] | null;
}

// skyTeam:天合聯盟 / starAlliance:星空聯盟 / oneworld:寰宇一家
// prettier-ignore
const alliances:any = {
    skyTeam: [
        'AF','AM','AR','AZ','CI','CZ','DL','GA','KE','KL','KQ','ME','MF','MU','OK','RO','SU','SV','UX','VN'
    ],
    starAlliance:[
        'AC','AI','AV','A3','BR','CA','CM','ET','JP','LH','LX','LO','MS','NH','NZ','OS','OU','OX','O6','SA','SK','SN','SQ','TG','TK','TP','UA','ZH'
    ],
    oneworld: [
        'AA','AY','BA','CX','IB','JJ','JL','LA','LP','LU','MH','PZ','QF','QR','RJ','S7','UL','XL','4C','4D','KA'
    ]
};

const Alliances = (props: I_AlliancesProps) => {
    const moduleName = 'alliances';
    const { airlines } = props;
    const { tempMFilter, setTempMFilter } = props;

    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });
    const dispatch = useDispatch();
    const { FilterData } = useSelector((state: RootState) => state.search);

    const deviceFilter = isMobile ? tempMFilter : FilterData;
    const setDeviceFilter = (newFilter: any) => {
        if (!isMobile) dispatch(setFilterData(newFilter));
        if (isMobile && setTempMFilter) setTempMFilter(newFilter);
    };

    const [initialAllianceItems, setInitialAllianceItems] = useState<string[]>([]);
    const [allianceItems, setAllianceItems] = useState<string[]>([]);

    // 根據搜尋結果所產出的【航空公司】欄位，取得需要的【航空聯盟欄】欄位
    const allianceBySearch: I_Alliances = useMemo(() => {
        if (!airlines) return null;

        const availableAlliance: any = {
            skyTeam: null,
            starAlliance: null,
            oneworld: null,
        };

        airlines.forEach((airline) => {
            Object.keys(alliances).forEach((key) => {
                if (!alliances[key].includes(airline.value.toString())) return;

                if (!availableAlliance[key]) availableAlliance[key] = [];

                availableAlliance[key].push(airline.value.toString());
            });
        });

        return availableAlliance;
    }, [airlines]);

    // 初始化欄位
    useEffect(() => {
        const initialAlliance = Object.keys(allianceBySearch).filter(
            (key: string) => allianceBySearch[key] !== null
        );

        setInitialAllianceItems(initialAlliance);
    }, [allianceBySearch]);

    // 計算能選擇的 Alliance 欄位
    useEffect(() => {
        if (!allianceBySearch) return;
        if (!deviceFilter || !deviceFilter.airport) return setAllianceItems([]);

        // Map 出 value 的部分
        const selectedAirports: string[] = deviceFilter.airport.map(
            (airport: { checked: boolean; value: string }) => {
                if (airport.checked) return airport.value;
            }
        );

        const resultAllianceItems: string[] = [];
        Object.keys(allianceBySearch).forEach((key) => {
            if (!selectedAirports) return;
            if (!allianceBySearch[key] || allianceBySearch[key]?.length === 0) return;

            if (
                !resultAllianceItems.includes(key) &&
                allianceBySearch[key]?.every((airport) => {
                    return selectedAirports.includes(airport);
                })
            ) {
                resultAllianceItems.push(key);
            }
        });

        if (allianceItems.length > 0 && isSameArr(resultAllianceItems, allianceItems)) return;

        setAllianceItems(resultAllianceItems);
    }, [FilterData, tempMFilter]);

    // 取得要顯示的 alliance title
    const getAllianceTitle = (alliance: string) => {
        let Cntitle, Entitle;

        switch (alliance) {
            case 'skyTeam':
                Cntitle = '天合聯盟';
                Entitle = 'SkyTeam';
                break;
            case 'starAlliance':
                Cntitle = '星空聯盟';
                Entitle = 'StarAlliance';
                break;
            case 'oneworld':
                Cntitle = '寰宇一家';
                Entitle = 'Oneworld';
                break;
            default:
                break;
        }

        return {
            Cntitle,
            Entitle,
        };
    };

    //點選航空聯盟按鈕
    const handleAllianceClick = (alliance: string) => {
        if (!airlines || isDisabled(alliance)) return;

        const newFilterData = deviceFilter ? JSON.parse(JSON.stringify(deviceFilter)) : {};
        let newAlliance = [...allianceItems];

        // 與該聯盟相關的航空公司列表
        const unionList = airlines.filter((airline) =>
            alliances[alliance].includes(airline.value.toString())
        );

        if (allianceItems.includes(alliance)) {
            // 移除
            unionList.forEach((union) => {
                if (!newFilterData.airport) return;

                newFilterData.airport = newFilterData.airport.filter(
                    (airport: { value: string }) => airport.value !== union.value
                );
            });

            newAlliance = newAlliance.filter((a) => a !== alliance);
        } else {
            // 新增
            unionList.forEach((union) => {
                if (newFilterData?.airport && newFilterData.airport.length > 0) {
                    newFilterData.airport.push({
                        text: union.text,
                        value: union.value,
                        checked: true,
                    });
                } else {
                    newFilterData.airport = [
                        {
                            text: union.text,
                            value: union.value,
                            checked: true,
                        },
                    ];
                }
            });

            newAlliance.push(alliance);
        }

        setAllianceItems(newAlliance);
        setDeviceFilter(newFilterData);
    };

    // 計算此 Alliance 欄位是否需要被 disable
    const isDisabled = (alliance: string) => {
        if (!airlines) return false;

        // 排除已選擇選項, 如果該聯盟的 airlines 選項全部都 disable: true
        // => disable 該選項
        if (!allianceItems.includes(alliance)) {
            const disableArr: any = [];
            airlines.forEach((airline) => {
                if (
                    allianceBySearch[alliance] &&
                    allianceBySearch[alliance]?.includes(airline.value.toString())
                ) {
                    disableArr.push(airline.disable); // boolean
                }
            });

            return disableArr.includes(true);
        }
    };

    return (
        <ul className={cN(styles.Alliance)}>
            {initialAllianceItems.length > 0 &&
                initialAllianceItems.map((alliance, i) => {
                    const { Cntitle, Entitle } = getAllianceTitle(alliance);
                    if (alliance) {
                        return (
                            <li
                                key={i}
                                className={cN({
                                    selected: allianceItems.includes(alliance),
                                    disabled: isDisabled(alliance),
                                })}
                                onClick={() => {
                                    handleAllianceClick(alliance);
                                }}
                            >
                                <div className={cN(styles.airline)}>
                                    <div className={cN(styles.checkBtn)}>
                                        {allianceItems.includes(alliance) && (
                                            <span className={cN('ic-ln icon toolchoosenf')} />
                                        )}
                                    </div>
                                    <div className={cN(styles.airWrap)}>
                                        <div className={cN(styles.cName)}>{Cntitle}</div>
                                        <div className={cN(styles.eName)}>{Entitle}</div>
                                    </div>
                                </div>
                            </li>
                        );
                    }
                })}
        </ul>
    );
};

export default Alliances;
